"use client";

import Link from "next/link";
import { useEarlyAccess } from "@/components/early-access";
import { cn } from "@/lib/utils";

function Arrow() {
  return (
    <svg
      className="transition-transform duration-200 group-hover:translate-x-0.5"
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M5 12h14M13 6l6 6-6 6" />
    </svg>
  );
}

const BASE =
  "group inline-flex items-center justify-center gap-2 rounded-lg px-5 py-2.5 font-sans text-sm font-medium transition-transform duration-200 hover:scale-[1.02] active:scale-[0.98]";

const VARIANT = {
  primary: "bg-foreground text-background",
  secondary:
    "bg-transparent text-foreground ring-1 ring-inset ring-border-strong hover:bg-foreground/[0.04]",
} as const;

/**
 * Button that opens the early-access modal. Same two skins and the same
 * default label as before; `variant` exists because this is now what the
 * marketing CTAs render, and they are not all filled.
 *
 * Since 2026-08-30 it also carries the "Get your API key" CTAs on the home
 * page, the PK Endpoint page, the pricing cards and the blog footer. Those
 * used to link to `/dashboard/keys`, which for a signed-out visitor is a
 * redirect to the login page: the wrong door while the product is gated
 * behind early access. **Only the CTAs changed.** `/dashboard` and
 * `/dashboard/login` are untouched and still reachable by typing the URL, on
 * purpose: this hides the entrance, it does not lock it. Reversing it means
 * putting `<LinkButton href="/dashboard/keys">` back at the call sites.
 */
export function GetEarlyAccessButton({
  className,
  children = "Get early access",
  variant = "primary",
  withArrow = true,
}: {
  className?: string;
  children?: React.ReactNode;
  variant?: keyof typeof VARIANT;
  withArrow?: boolean;
}) {
  const { open } = useEarlyAccess();
  return (
    <button type="button" onClick={open} className={cn(BASE, VARIANT[variant], className)}>
      {children}
      {withArrow && <Arrow />}
    </button>
  );
}

/**
 * A link styled as a button, in the same two skins as the modal trigger, so
 * "Get your API key" beside "Get early access" reads as one family.
 *
 * Internal hrefs go through `next/link`; anything with a scheme (the docs
 * host) is a plain anchor in a new tab. Hash links stay plain anchors too:
 * `next/link` would route them, which on the home page is a no-op with a
 * flash, where the browser's own anchor scroll is what we want.
 */
export function LinkButton({
  href,
  children,
  variant = "primary",
  withArrow = true,
  className,
}: {
  href: string;
  children: React.ReactNode;
  variant?: keyof typeof VARIANT;
  withArrow?: boolean;
  className?: string;
}) {
  const classes = cn(BASE, VARIANT[variant], className);
  const external = /^[a-z]+:/i.test(href);
  if (external) {
    return (
      <a href={href} target="_blank" rel="noopener noreferrer" className={classes}>
        {children}
        {withArrow && <Arrow />}
        <span className="sr-only">(opens in a new tab)</span>
      </a>
    );
  }
  if (href.startsWith("#") || href.startsWith("/#")) {
    return (
      <a href={href} className={classes}>
        {children}
        {withArrow && <Arrow />}
      </a>
    );
  }
  return (
    <Link href={href} className={classes}>
      {children}
      {withArrow && <Arrow />}
    </Link>
  );
}

/**
 * `API_KEY_HREF`, which pointed "Get your API key" at `/dashboard/keys`, was
 * removed 2026-08-30. Every one of those CTAs now renders
 * `GetEarlyAccessButton` instead, so no marketing page sends a visitor to a
 * sign-in they cannot complete. The constant is gone rather than left unused
 * so nothing quietly picks it up again; the routes it named still work when
 * typed. See the note on `GetEarlyAccessButton`.
 */
